import {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
  memo,
  KeyboardEvent,
  ChangeEvent,
} from "react";
import { cn } from "../../lib/utils.js";
import { Trash, ArrowUp, ArrowDown } from "lucide-react";

const ArrowIcon = memo(({ dir }: { dir: "up" | "down" | "both" }) => {
  switch (dir) {
    case "up":
      return (
        <svg className="fill-muted size-3" viewBox="0 0 24 24">
          <path
            d="M12 2.25l7.636 7.676a.75.75 0 01-1.06 1.06L12 4.06 5.424 10.986a.75.75 0 11-1.06-1.06L12 2.25z"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    case "down":
      return (
        <svg className="fill-muted size-3" viewBox="0 0 24 24">
          <path
            d="M12 21.75l-7.636-7.676a.75.75 0 011.06-1.06L12 19.94l6.576-6.986a.75.75 0 011.06 1.06L12 21.75z"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    default:
      return (
        <svg width={16} height={16} viewBox="0 0 16 16" className="fill-muted">
          <path
            d="M2 10.667 4.667 13.334 7.333 10.667M4.667 13.334V2.667M14 5.334 11.333 2.667 8.667 5.334M11.333 2.667v10.667"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
  }
});
ArrowIcon.displayName = "ArrowIcon";

export interface EditableTableRow {
  label: string;
  id?: string;
  [key: string]: string | number | undefined;
}

interface EditableTableProps {
  title: string;
  data: EditableTableRow[];
  rowIdKey?: keyof EditableTableRow;
  columnsName?: string[];
  onAdd?: (
    row: Omit<EditableTableRow, "label" | "id">
  ) => Promise<EditableTableRow> | EditableTableRow;
  onEdit?: (
    row: EditableTableRow,
    key: string,
    newValue: string
  ) => Promise<EditableTableRow> | EditableTableRow;
  onDelete?: (rows: EditableTableRow[]) => Promise<void> | void;
  reorder?: boolean;
  onSaveReorder?: (_: EditableTableRow[]) => void;
  onColumnsNameChange?: (names: string[]) => void;
}

type SortConfig = { key: string; direction: "asc" | "desc" };
type EditingCell = { rowId: string; key: string } | null;

const isBlankRow = (row: EditableTableRow, skipKeys: Set<string>) =>
  Object.keys(row).every((k) => skipKeys.has(k) || !row[k]);

export default function EditableTable({
  title,
  data,
  rowIdKey = "id", // <-- default 'id'
  columnsName,
  onAdd,
  onEdit,
  onDelete,
  reorder = false,
  onSaveReorder,
  onColumnsNameChange,
}: EditableTableProps) {
  const [rows, setRows] = useState<EditableTableRow[]>(data);
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);
  const [editingCell, setEditingCell] = useState<EditingCell>(null);
  const [editingValue, setEditingValue] = useState("");
  const tempIds = useRef<Set<string>>(new Set());

  const [localColumnsName, setLocalColumnsName] = useState<string[] | undefined>(columnsName);
  useEffect(() => setLocalColumnsName(columnsName), [columnsName]);

  const [editingHeaderIndex, setEditingHeaderIndex] = useState<number | null>(null);
  const [editingHeaderValue, setEditingHeaderValue] = useState("");

  useEffect(() => setRows(data), [data]);

  const skipKeySet = useMemo(() => new Set<string>(["label", String(rowIdKey)]), [rowIdKey]);

  const dynamicKeys = useMemo(() => {
    if (rows[0]) return Object.keys(rows[0]).filter((k) => !skipKeySet.has(k));
    return columnsName ?? [];
  }, [rows, skipKeySet, columnsName]);

  const sortedRows = useMemo(() => {
    if (!sortConfig) return rows;
    const { key, direction } = sortConfig;
    return [...rows].sort((a, b) => {
      const av = a[key],
        bv = b[key];
      const aBlank = av == null || av === "";
      const bBlank = bv == null || bv === "";
      if (aBlank || bBlank) return aBlank === bBlank ? 0 : aBlank ? 1 : -1;

      const cmp =
        typeof av === "number" && typeof bv === "number"
          ? (av as number) - (bv as number)
          : av!.toString().localeCompare(bv!.toString());

      return direction === "asc" ? cmp : -cmp;
    });
  }, [rows, sortConfig]);

  const getId = useCallback((row: EditableTableRow) => String(row[rowIdKey]), [rowIdKey]);

  const startInlineEdit = useCallback(
    (rowId: string, key: string, initialValue: string | number | undefined) => {
      setEditingCell({ rowId, key });
      setEditingValue(String(initialValue ?? ""));
    },
    []
  );

  const addRow = useCallback(() => {
    // Avoid spawning a new row while editing an existing cell
    if (editingCell) return;

    const keys = dynamicKeys.length ? dynamicKeys : columnsName ?? [];
    const unfinished = rows.find((r) => isBlankRow(r, skipKeySet));
    if (unfinished) {
      const firstKey = keys[0];
      if (firstKey) {
        startInlineEdit(getId(unfinished), firstKey, "");
      }
      return;
    }

    const nextLabel =
      rows.reduce((max, r) => Math.max(max, parseInt(String(r.label), 10) || 0), 0) + 1;

    const tempId = `tmp-${Date.now()}-${nextLabel}`;
    const blank: EditableTableRow = { id: tempId, label: String(nextLabel) };
    keys.forEach((k) => (blank[k] = ""));

    tempIds.current.add(tempId);
    setRows((prev) => [...prev, blank]);
    const firstKey = keys[0];
    if (firstKey) {
      setTimeout(() => startInlineEdit(tempId, firstKey, ""), 0);
    }
  }, [rows, dynamicKeys, skipKeySet, getId, startInlineEdit, columnsName, editingCell]);

  const deleteRow = useCallback(
    async (row: EditableTableRow) => {
      // Keep placeholder/blank rows intact
      if (rows.length === 1 && isBlankRow(row, skipKeySet)) return;

      const id = getId(row);
      if (tempIds.current.has(id)) tempIds.current.delete(id);

      setRows((prev) => prev.filter((r) => getId(r) !== id));

      await onDelete?.([row]);
    },
    [getId, onDelete, rows.length, skipKeySet]
  );

  const commitEdit = useCallback(async () => {
    if (!editingCell) return;

    const { rowId, key } = editingCell;
    const value = editingValue.trim();
    const isTemp = tempIds.current.has(rowId);

    try {
      if (isTemp) {
        if (value && onAdd) {
          const tempRow = rows.find((r) => getId(r) === rowId)!;
          const payload = Object.fromEntries(
            Object.entries({ ...tempRow, [key]: value }).filter(
              ([entryKey]) => entryKey !== "label" && entryKey !== "id"
            )
          ) as Omit<EditableTableRow, "label" | "id">;

          const saved = await onAdd(payload);
          setRows((prev) => prev.map((r) => (getId(r) === rowId ? { ...r, ...saved } : r)));
          tempIds.current.delete(rowId);
        } else {
          setRows((prev) => prev.filter((r) => getId(r) !== rowId));
          tempIds.current.delete(rowId);
        }
      } else if (onEdit) {
        const base = rows.find((r) => getId(r) === rowId)!;
        const saved = await onEdit(base, key, value);
        setRows((prev) => prev.map((r) => (getId(r) === rowId ? { ...r, ...saved } : r)));
      } else {
        setRows((prev) => prev.map((r) => (getId(r) === rowId ? { ...r, [key]: value } : r)));
      }
    } catch (e) {
      console.error("commitEdit failed", e);
    } finally {
      setEditingCell(null);
      setEditingValue("");
    }
  }, [editingCell, editingValue, rows, onAdd, onEdit, getId]);

  const cancelEdit = useCallback(() => {
    if (!editingCell) return;
    const { rowId } = editingCell;
    if (tempIds.current.has(rowId)) {
      setRows((prev) => prev.filter((r) => getId(r) !== rowId));
      tempIds.current.delete(rowId);
    } else {
      const original = data.find((r) => getId(r) === rowId);
      if (original) {
        setRows((prev) => prev.map((r) => (getId(r) === rowId ? original : r)));
      }
    }
    setEditingCell(null);
    setEditingValue("");
  }, [editingCell, data, getId]);

  const startHeaderEdit = useCallback((index: number, initial: string) => {
    setEditingHeaderIndex(index);
    setEditingHeaderValue(initial);
  }, []);

  const commitHeaderEdit = useCallback(() => {
    if (editingHeaderIndex == null) return;
    const next = (editingHeaderValue ?? "").trim();

    setLocalColumnsName((prev) => {
      const base = prev && prev.length ? [...prev] : [...(columnsName ?? dynamicKeys)];
      base[editingHeaderIndex] = next;
      onColumnsNameChange?.(base);
      return base;
    });

    setEditingHeaderIndex(null);
    setEditingHeaderValue("");
  }, [editingHeaderIndex, editingHeaderValue, columnsName, dynamicKeys, onColumnsNameChange]);

  const cancelHeaderEdit = useCallback(() => {
    setEditingHeaderIndex(null);
    setEditingHeaderValue("");
  }, []);

  const headBase =
    "px-2 py-2 text-xs font-medium tracking-wide whitespace-nowrap uppercase select-none text-[#71717A]";
  const labelHead = cn(headBase, "w-[20px] text-left");
  const dynHead = cn(headBase, "cursor-pointer text-left");

  const moveRow = useCallback(
    (rowId: string, newIndex: number) => {
      setRows((prev) => {
        const index = prev.findIndex((r) => getId(r) === rowId);
        if (index === -1 || newIndex < 0 || newIndex >= prev.length) return prev;

        const updated = [...prev];
        const [moved] = updated.splice(index, 1);
        updated.splice(newIndex, 0, moved as EditableTableRow);
        if (onSaveReorder) {
          onSaveReorder(updated);
        }
        return updated;
      });
    },
    [getId, onSaveReorder]
  );

  return (
    <div className="inline-block flex-1 overflow-hidden rounded-md border border-[--ft-base-border]">
      <table className="w-full table-auto border-collapse text-sm">
        <thead className="border-b border-b-[#E4E4E7] bg-[--bgb]">
          <tr className="w-100">
            <th className={`${labelHead} w-100`}>
              <div className="flex items-center justify-between gap-2">
                <span>{title}</span>
                {dynamicKeys.map((key, i) => {
                  const sorted = sortConfig?.key === key;
                  const direction = sorted ? sortConfig!.direction : undefined;
                  const displayName = localColumnsName?.[i] ?? columnsName?.[i] ?? key;

                  return (
                    <div
                      key={key}
                      className={`${dynHead} flex w-full justify-between`}
                      onClick={() =>
                        !reorder
                          ? setSortConfig((prev) =>
                              prev?.key === key
                                ? { key, direction: prev.direction === "asc" ? "desc" : "asc" }
                                : { key, direction: "asc" }
                            )
                          : undefined
                      }
                    >
                      {editingHeaderIndex === i ? (
                        <input
                          className="w-full max-w-[180px] rounded border px-1 py-0.5 outline-none"
                          value={editingHeaderValue}
                          onChange={(e) => setEditingHeaderValue(e.target.value)}
                          onBlur={commitHeaderEdit}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.stopPropagation();
                              commitHeaderEdit();
                            }
                            if (e.key === "Escape") {
                              e.stopPropagation();
                              cancelHeaderEdit();
                            }
                          }}
                          autoFocus
                        />
                      ) : (
                        <button
                          className="truncate text-left"
                          onClick={(e) => {
                            e.stopPropagation();
                            startHeaderEdit(i, displayName);
                          }}
                          title="Edit column name"
                        >
                          {displayName}
                        </button>
                      )}

                      {!reorder && (
                        <span className="flex items-center gap-1">
                          {sorted ? (
                            <ArrowIcon dir={direction === "asc" ? "up" : "down"} />
                          ) : (
                            <ArrowIcon dir="both" />
                          )}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </th>
          </tr>
        </thead>

        <tbody className="divide-y divide-[--ft-base-border]">
          {sortedRows.length === 0 && (
            <tr>
              <td
                colSpan={dynamicKeys.length + 1}
                className="text-muted-foreground px-4 py-4 text-center"
              >
                No data
              </td>
            </tr>
          )}

          {sortedRows.map((row, idx) => {
            const id = getId(row);
            return (
              <EditableRow
                key={`${id}-${idx}`}
                idx={idx}
                row={row}
                id={id}
                dynamicKeys={dynamicKeys}
                editingCell={editingCell}
                editingValue={editingValue}
                startInlineEdit={startInlineEdit}
                setEditingValue={setEditingValue}
                commitEdit={commitEdit}
                cancelEdit={cancelEdit}
                onDelete={onDelete}
                deleteRow={deleteRow}
                lastRow={idx === sortedRows.length - 1}
                onChangePosition={moveRow}
                reorder={reorder}
              />
            );
          })}

          <tr
            tabIndex={0}
            onClick={addRow}
            onKeyDown={(e) => {
                // Only trigger add from Enter/Space when the focus is on the add row itself
                if (e.key === "Enter" || e.key === " " || e.key === "Spacebar") {
                  e.preventDefault();
                  addRow();
                }
            }}
            className="h-13 cursor-text bg-white"
          >
            <td
              colSpan={dynamicKeys.length + (onDelete ? 1 : 0)}
              className="px-4 py-3 text-xs text-[#9CA3AF]"
            >
              TYPE TO ADD ROW
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

interface RowProps {
  idx: number;
  id: string;
  row: EditableTableRow;
  dynamicKeys: string[];
  editingCell: EditingCell;
  editingValue: string;
  startInlineEdit: (rowId: string, key: string, initialValue: string | number | undefined) => void;
  setEditingValue: (v: string) => void;
  commitEdit: () => void;
  cancelEdit: () => void;
  onDelete?: EditableTableProps["onDelete"];
  deleteRow: (r: EditableTableRow) => void;
  lastRow?: boolean;
  onChangePosition?: (rowId: string, index: number) => void;
  reorder?: boolean;
}

const EditableRow = memo<RowProps>(
  ({
    idx,
    id,
    row,
    dynamicKeys,
    editingCell,
    editingValue,
    startInlineEdit,
    setEditingValue,
    commitEdit,
    cancelEdit,
    onDelete,
    deleteRow,
    lastRow,
    onChangePosition,
    reorder = false,
  }) => {
    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        e.preventDefault();
        e.stopPropagation();
        commitEdit();
      }
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        cancelEdit();
      }
    };

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => setEditingValue(e.target.value);

    return (
      <tr className="hover:bg-gray-50">
        {dynamicKeys.map((k) => {
          const editing = editingCell?.rowId === id && editingCell.key === k;
          const val = row[k] ?? "";

          return (
            <td
              key={k}
              className="bg-white px-2 py-1 text-left"
              onDoubleClick={() => !editing && startInlineEdit(id, k, val)}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center">
                  <span className="select-none bg-white px-2 py-1 text-left">
                    {row.id?.split("-")[row.id?.split("-").length - 1]}
                  </span>
                  {editing ? (
                    <input
                      className="w-full rounded border px-1 py-0.5 outline-none"
                      value={editingValue}
                      onChange={handleChange}
                      onBlur={commitEdit}
                      onKeyDown={handleKeyDown}
                      autoFocus
                    />
                  ) : (
                    <span>{val}</span>
                  )}
                </div>
                <div className="flex items-center justify-items-end gap-1">
                  {onDelete && (
                    <button
                      onClick={() => deleteRow(row)}
                      className="cursor-pointer rounded hover:bg-red-100"
                      title="Eliminar fila"
                    >
                      <Trash width={15} height={15} className="h-4 w-4 text-red-500" />
                    </button>
                  )}
                  {idx !== 0 && reorder && (
                    <button
                      className="cursor-pointer rounded hover:bg-gray-100"
                      title="Mover arriba"
                      onClick={() => {
                        console.log(idx);
                        if (onChangePosition) onChangePosition(id, idx - 1);
                      }}
                    >
                      <ArrowUp width={15} height={15} />
                    </button>
                  )}
                  {!lastRow && reorder && (
                    <button
                      className="cursor-pointer rounded hover:bg-gray-100"
                      title="Mover abajo"
                      onClick={() => {
                        if (onChangePosition) onChangePosition(id, idx + 1);
                      }}
                    >
                      <ArrowDown width={15} height={15} />
                    </button>
                  )}
                </div>
              </div>
            </td>
          );
        })}
      </tr>
    );
  },
  (prev, next) =>
    prev.row === next.row &&
    prev.editingCell === next.editingCell &&
    prev.editingValue === next.editingValue
);

EditableRow.displayName = "EditableRow";
